import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { EventCategory } from '../../types/event';

interface CategoryDropdownProps {
  options: EventCategory[];
  value?: number;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean;
  className?: string;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select category...',
  label,
  error,
  required = false,
  className = ''
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Get selected category
  const selectedCategory = options.find(category => category.id === value);

  // Shadow detection logic (similar to CustomLegend)
  useEffect(() => {
    const container = listRef.current;
    if (!container || !isOpen) {
      setShowTopShadow(false);
      setShowBottomShadow(false);
      return;
    }

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // -1 for rounding errors
      const hasScroll = scrollHeight > clientHeight;

      setShowTopShadow(!isAtTop && hasScroll);
      setShowBottomShadow(!isAtBottom && hasScroll);
    };

    // Check initial state
    checkScroll();

    // Add scroll event listener
    container.addEventListener('scroll', checkScroll);
    
    // Also check on resize
    window.addEventListener('resize', checkScroll);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [isOpen, options]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle option selection
  const handleOptionSelect = (category: EventCategory | null) => {
    if (category) {
      onChange(category.id);
    } else {
      onChange(undefined);
    }
    setIsOpen(false);
    setIsFocused(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < options.length ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > -1 ? prev - 1 : options.length
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === -1) {
          handleOptionSelect(null);
        } else if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current && isOpen) {
      const highlightedElement = listRef.current.children[highlightedIndex + 1] as HTMLElement; // +1 for placeholder option
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  const shadowGradient = theme === 'dark' 
    ? 'from-gray-800 via-gray-800/80 to-transparent'
    : 'from-white via-white/80 to-transparent';

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className={`block text-sm font-semibold mb-2 ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Select Button */}
      <div
        className={`w-full px-4 py-3 rounded-lg border-2 transition-all cursor-pointer ${
          isFocused || isOpen
            ? 'ring-2 ring-mirsal-green ring-opacity-20 border-mirsal-green'
            : ''
        } ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
        } ${error ? 'border-red-500' : ''}`}
        onClick={() => {
          setIsOpen(!isOpen);
          setIsFocused(!isOpen);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          // Delay blur to allow click events to process
          setTimeout(() => {
            if (!isOpen) {
              setIsFocused(false);
            }
          }, 200);
        }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="category-dropdown-list"
      >
        <div className="flex items-center justify-between">
          <span className={
            selectedCategory ? '' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }>
            {selectedCategory ? selectedCategory.name : placeholder}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${
            isOpen ? 'rotate-180' : ''
          } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden ${
          theme === 'dark' 
            ? 'bg-gray-800 border border-gray-700' 
            : 'bg-white border border-gray-200'
        }`}>
          {/* Scrollable container with shadows */}
          <div className="relative max-h-[400px]">
            {/* Top shadow */}
            {showTopShadow && (
              <div 
                className={`absolute top-0 left-0 right-0 h-8 pointer-events-none z-10 bg-gradient-to-b ${shadowGradient}`}
              />
            )}
            
            {/* Options List */}
            <div 
              ref={listRef}
              id="category-dropdown-list"
              className="overflow-y-auto scrollbar-thin max-h-[400px]"
              role="listbox"
            >
              {/* Placeholder option */}
              <div
                className={`px-4 py-3 cursor-pointer text-sm ${
                  highlightedIndex === -1
                    ? theme === 'dark' 
                      ? 'bg-gray-700 text-white' 
                      : 'bg-gray-100 text-gray-900'
                    : theme === 'dark' 
                      ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                } ${
                  !value ? 'bg-mirsal-green/10 text-mirsal-green' : ''
                }`}
                onClick={() => handleOptionSelect(null)}
                role="option"
                aria-selected={!value}
              >
                {placeholder}
              </div>

              {/* Category options */}
              {options.map((category, index) => (
                <div
                  key={category.id}
                  className={`px-4 py-3 cursor-pointer text-sm ${
                    index === highlightedIndex
                      ? theme === 'dark' 
                        ? 'bg-gray-700 text-white' 
                        : 'bg-gray-100 text-gray-900'
                      : theme === 'dark' 
                        ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  } ${
                    category.id === value ? 'bg-mirsal-green/10 text-mirsal-green' : ''
                  }`}
                  onClick={() => handleOptionSelect(category)}
                  role="option"
                  aria-selected={category.id === value}
                >
                  {category.name}
                </div>
              ))}
            </div>

            {/* Bottom shadow */}
            {showBottomShadow && (
              <div 
                className={`absolute bottom-0 left-0 right-0 h-8 pointer-events-none z-10 bg-gradient-to-t ${shadowGradient}`}
              />
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default CategoryDropdown;

